import { CriApiResponse, CadastroCriApiRequest, InstrumentoFinanceiro, TipoRegistroInstrumentoFinanceiro, ReservarUltimoNumeroDaEmissaoRequest } from 'common/genericTypes';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { RootState } from 'store';
import { resetCRIDetails, updateCRICaracteristicas, updateField } from 'store/modules/cri/caracteristicas';
import { resetCriteriosRemuneracao, updateCriteriosRemuneracao } from 'store/modules/cri/criteriosRemuneracao';
import { resetEventos, updateEventos } from 'store/modules/cri/eventos';
import { resetDocuments, updateDocuments } from 'store/modules/cri/uploadDocumentos';
import { updateId, resetEmissor } from 'store/modules/cri/emissor';
import { UploadDocResponse } from 'store/modules/models/uploadDocResponse';
import CriParser from 'utils/parsers/criParser';
import { addAlert } from 'store/modules/snackBar';
import { highlightErrors, typeOfRegister } from 'store/modules/cri';
import Formulario from 'pages/cri/registro/formulario';

type ComponentParams = { criId?: string };
type ComponentState = { criRascunho?: CriApiResponse; acao?: TipoRegistroInstrumentoFinanceiro };

type Props = RouteComponentProps<ComponentParams, {}, ComponentState>;

const Cri: React.FC<Props> = (props) => {
  const { criId } = props.match.params;
  const criRascunho = props.location.state?.criRascunho;

  const history = useHistory();
  const dispatch = useDispatch();
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);

  const caracteristicas = useSelector((state: RootState) => state.criCadastro.caracteristicas);
  const criteriosRemuneracao = useSelector((state: RootState) => state.criCadastro.criteriosRemuneracao);
  const eventos = useSelector((state: RootState) => state.criCadastro.eventos);
  const uploadDocumentos = useSelector((state: RootState) => state.criCadastro.uploadDocumentos);
  const emissor = useSelector((state: RootState) => state.criCadastro.emissor);
  const criApi = useSelector((state: RootState) => state.criApi);
  const instrumentoApi = useSelector((state: RootState) => state.instrumentoApi);

  const [codigo, setCodigo] = useState<string>('');
  const [typeOfFormulario, setTypeOfFormulario] = useState<TipoRegistroInstrumentoFinanceiro>(TipoRegistroInstrumentoFinanceiro.Cadastro);

  const submitPutCri = async (
    criId: string,
    cri: CadastroCriApiRequest
  ) => {
    let result = { codigo: '', ticket: '' };
    try {
      const res = await criApi.putCri(dispatch, criId, cri, true);
      result = { codigo: res.codigo, ticket: res.ticket };
    } catch (error) { }
    return result;
  };

  const submitPostCri = async (): Promise<CriApiResponse> => {
    try {
      const cri: CadastroCriApiRequest = CriParser.stateToCadastroCriApiRequest(
        caracteristicas,
        criteriosRemuneracao,
        eventos,
        uploadDocumentos,
        emissor
      );
      return await criApi.postCri(dispatch, cri, true);
    } catch (error) {
      return {} as CriApiResponse;
    }
  };

  const submitCriDocument = async (criId: string, emissionType: string, file: File): Promise<UploadDocResponse[]> => {
    try {
      return criApi.putUploadDoc(dispatch, criId, emissionType, file, true);
    } catch (error) {
      return [];
    }
  };

  const handleEditSubmit = async () => {
    if (!criId) return { codigo: '', ticket: '' };

    const cri: CadastroCriApiRequest = CriParser.stateToCadastroCriApiRequest(
      caracteristicas,
      criteriosRemuneracao,
      eventos,
      uploadDocumentos,
      emissor
    );

    const criResponse = await submitPutCri(criId, cri);

    for (const document of uploadDocumentos.documents) {
      if (document.file) {
        const uploadResponse = await submitCriDocument(criId, document.type, document.file);
        if (!uploadResponse || uploadResponse.length === 0) return { codigo: '', ticket: '' };
      }
    }

    if (caracteristicas.id) {
      for (const documentToDelete of uploadDocumentos.documentsToDelete) {
        documentToDelete.remoteFileInfo?.id &&
          (await criApi.deleteUploadDoc(dispatch, criId, documentToDelete.remoteFileInfo.id, true));
      }
    }

    return criResponse;
  };

  const handleRegisterSubmit = async () => {
    const uploadedCri = await submitPostCri();

    if (uploadedCri.id) {
      for (const document of uploadDocumentos.documents) {
        if (document.file) {
          await submitCriDocument(uploadedCri.criId, document.type, document.file);
        }
      }
    }

    return { codigo: '', ticket: uploadedCri.ticket };
  };

  const handleAditamentoSubmit = async (): Promise<{ codigo: string, ticket: string } | undefined> => {
    try {
      if (codigo) {
        return { codigo, ticket: '' };
      } else throw 'ID do CRI não identificado';
    } catch (error) {
      console.error(error);
    }
  };

  const handleSalvarRascunho = async () => {
    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return '';
    }
    if (!caracteristicas?.cnpjEmissor) {
      dispatch(addAlert({ message: 'CNPJ do emissor não encontrado', severity: 'error' }));
      return '';
    }
    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjEmissor,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.Cri,
        externalId: participanteId,
      };

      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return '';
    }
    let draftId = '';
    try {
      const rascunho = CriParser.stateToCadastroCriApiRequest(
        caracteristicas,
        criteriosRemuneracao,
        eventos,
        uploadDocumentos,
        emissor
      );

      const documents = uploadDocumentos.documents
        .filter((doc) => !!doc.file)
        .map((doc) => ({ file: doc.file!, emissionType: doc.type }));

      const result = caracteristicas.draftId
        ? await instrumentoApi.putRascunho(
          dispatch,
          InstrumentoFinanceiro.Cri,
          caracteristicas.draftId,
          rascunho,
          documents,
          true
        )
        : await instrumentoApi.postRascunho(dispatch, InstrumentoFinanceiro.Cri, rascunho, documents, true);
      setCodigo(result.codigo);
      draftId = result.draftId;
      dispatch(updateField({ name: 'draftId', value: draftId }));

      dispatch(addAlert({ message: `Rascunho salvo com sucesso. Código do Instrumento Financeiro ${result.codigo}`, severity: 'success' }));
    } catch (error) { }
    return draftId;
  };

  const reserveBeforeHandleSubmit = async (handleSubmit: () => Promise<{ codigo: string; ticket: string } | undefined>): Promise<{ codigo: string; ticket: string } | undefined> => {

    const numeroEmissaoReservado = await reservarNumeroEmissao();
    if (!numeroEmissaoReservado) return;
    return await handleSubmit();
  }

  const reservarNumeroEmissao = async () => {
    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return false;
    }
    if (!caracteristicas?.cnpjEmissor) {
      dispatch(addAlert({ message: 'CNPJ do emissor não encontrado', severity: 'error' }));
      return false;
    }

    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjEmissor,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.Cri,
        externalId: participanteId,
      };

      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
      return true;
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return false;
    }
  };

  const handleReturnToList = () => {
    history.push('/solicitacoes');
  };

  const defineTypeOfRegister = async (): Promise<void> => {
    if (criId) {
      if (props.location.state?.acao) {
        setTypeOfFormulario(props.location.state.acao);
        dispatch(typeOfRegister(props.location.state.acao));
      }
    } else {
      setTypeOfFormulario(TipoRegistroInstrumentoFinanceiro.Cadastro);
      dispatch(typeOfRegister(TipoRegistroInstrumentoFinanceiro.Cadastro));
    }
  };

  useEffect(() => {
    dispatch(resetEmissor());
    dispatch(resetCRIDetails());
    dispatch(resetCriteriosRemuneracao());
    dispatch(resetEventos());
    dispatch(resetDocuments());
    dispatch(highlightErrors(false));
  }, []);

  useEffect(() => {
    if (criId) {
      criApi
        .getCriDetalhes(dispatch, criId, true)
        .then((response) => {
          if (response) {
            setCodigo(response.codigo);
            dispatch(updateId(response.emissorId ?? ''));
            dispatch(updateCRICaracteristicas(CriParser.apiResponseToCaracteristicasState(response)));
            dispatch(updateCriteriosRemuneracao(CriParser.apiResponseToCriteriosRemuneracaoState(response)));
            dispatch(updateEventos(CriParser.apiResponseToEventosState(response)));
            dispatch(updateDocuments(CriParser.apiResponseToCriDocumentsState(response)));
          }
        })
        .catch(() => { });
    } else if (criRascunho) {
      dispatch(updateId(criRascunho.emissorId ?? ''));
      dispatch(updateCRICaracteristicas(CriParser.apiResponseToCaracteristicasState(criRascunho, false)));
      dispatch(updateCriteriosRemuneracao(CriParser.apiResponseToCriteriosRemuneracaoState(criRascunho)));
      dispatch(updateEventos(CriParser.apiResponseToEventosState(criRascunho)));
      dispatch(updateDocuments(CriParser.apiResponseToCriDocumentsState(criRascunho)));
    }
    defineTypeOfRegister();
  }, [criApi, criId, criRascunho, dispatch]);

  const component = () => {
    switch (typeOfFormulario) {
      case TipoRegistroInstrumentoFinanceiro.Cadastro:
        return (
          <div>
            <Formulario
              title={`Cadastro CRI`}
              criId={criId}
              handleSubmit={() => reserveBeforeHandleSubmit(handleRegisterSubmit)}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={false}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Edicao:
        return (
          <div>
            <Formulario
              title={`Editar CRI`}
              criId={criId}
              handleSubmit={handleEditSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.EdicaoEmitido:
        return (
          <div>
            <Formulario
              title={`Editar ${codigo}`}
              criId={criId}
              handleSubmit={handleAditamentoSubmit}
              handleReturn={() => history.push('/instrumentos/gerenciar')}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
              codigo={codigo}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Aditamento:
        return (
          <div>
            <Formulario
              title={`Aditar CRI ${codigo}`}
              criId={criId}
              handleSubmit={handleAditamentoSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
              codigo={codigo}
            />
          </div>
        );
      default:
        return <p>Loading</p>
    }
  };
  return component();
};

export default Cri;
